import { useMutation } from "@tanstack/react-query";
import {
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Switch,
  message,
} from "antd";
import React from "react";
import { addAlbum, getImage, updateAlbum } from "../../utils/api";

import { X } from "lucide-react";
import BackendUpload from "../BackendUpload";

const AddAlbum = ({ update, artists, refetch, categories }) => {
  const [form] = Form.useForm();
  const [open, setOpen] = React.useState(false);
  const { mutate, isPending } = useMutation({
    mutationFn: async (values) =>
      update ? await updateAlbum(update?._id, values) : await addAlbum(values),
    onSuccess: () => {
      !update && form.resetFields();
      refetch();
      message.success(`Successfully ${update ? "Updated" : "Added"}`);
      setOpen(false);
    },
  });

  const type = Form.useWatch("type", form);
  return (
    <div>
      <Button
        type="primary"
        onClick={() => {
          setOpen(true);
        }}
        style={{
          width: "100%",
        }}
      >
        {update ? "Edit" : "Add"}
      </Button>
      <Modal
        title={update ? "Edit" : "Add"}
        open={open}
        width={600}
        onOk={() => {
          setOpen(false);
        }}
        onCancel={() => {
          setOpen(false);
        }}
        footer={null}
      >
        <Form
          form={form}
          labelCol={{ span: 6 }}
          layout="horizontal"
          disabled={isPending}
          style={{
            maxWidth: 900,
            padding: 20,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
          initialValues={
            update && {
              name: update.name,

              releaseYear: update.releaseYear,
              price: update.price,
              validity: update.validity,
              type: update.isFree ? true : false,
              genres: update.genres.map((item) => item._id),
              artist: update.artist.map((item) => item._id),
              featuringArtist: update.featuringArtist.map((item) => item._id),
              songs: update.songs.map((song) => ({
                name: song.name,
                artist: song.artist.map((item) => item._id),
                audio: song.audio,
                video: song.video,
              })),
            }
          }
          onFinish={(values) => {
            console.log(values);
            mutate({
              ...values,
              isPaid: values?.type ? false : true,
              isFree: values?.type ? true : false,

              cover: getImage(values?.cover) ?? update?.cover,
              songs: values?.songs?.map((song) => ({
                ...song,
                audio: getImage(song?.audio) ?? song?.audio,
                video: getImage(song?.video) ?? song?.video,
              })),
            });
          }}
        >
          <Form.Item label="Name" name="name" required>
            <Input />
          </Form.Item>

          <BackendUpload
            label={"Cover"}
            type={"photos"}
            name={"cover"}
            update={update}
          />

          <Form.Item label="Release Year" name="releaseYear" required>
            <InputNumber style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item label="Type" name="type" required>
            <Switch checkedChildren="Free" unCheckedChildren="Paid" />
          </Form.Item>
          {!type && (
            <>
              <Form.Item label="Price" name="price" required>
                <InputNumber style={{ width: "100%" }} />
              </Form.Item>
              <Form.Item label="Validity" name="validity" required>
                <InputNumber style={{ width: "100%" }} />
              </Form.Item>
            </>
          )}

          <Form.Item
            label="Genres"
            name="genres"
            rules={[{ required: true, message: "Genres is required" }]}
          >
            <Select mode="multiple" placeholder="Select Genres">
              {categories?.map((item) => (
                <Select.Option key={item._id} value={item._id}>
                  {item.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            label="Artist"
            name="artist"
            rules={[{ required: true, message: "Artist is required" }]}
          >
            <Select placeholder="Select Artist" mode="multiple">
              {artists?.map((item) => (
                <Select.Option key={item._id} value={item._id}>
                  {item.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            label="Featuring Artist"
            name="featuringArtist"
            rules={[{ required: true, message: "Artist is required" }]}
          >
            <Select placeholder="Select Artist" mode="multiple">
              {artists?.map((item) => (
                <Select.Option key={item._id} value={item._id}>
                  {item.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.List name="songs">
            {(fields, { add, remove }) => (
              <div
                style={{ display: "flex", rowGap: 16, flexDirection: "column" }}
              >
                {fields.map((field) => (
                  <Card
                    size="small"
                    title={`Song ${field.name + 1}`}
                    key={field.key}
                    extra={
                      <X
                        onClick={() => {
                          remove(field.name);
                        }}
                      />
                    }
                  >
                    <Form.Item
                      label="Name"
                      name={[field.name, "name"]}
                      required
                      rules={[{ required: true, message: "Name is required" }]}
                    >
                      <Input />
                    </Form.Item>
                    <Form.Item
                      label="Artist"
                      name={[field.name, "artist"]}
                      rules={[
                        { required: true, message: "Artist is required" },
                      ]}
                    >
                      <Select placeholder="Select Artist" mode="multiple">
                        {artists?.map((item) => (
                          <Select.Option key={item._id} value={item._id}>
                            {item.name}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>

                    <BackendUpload
                      label={"Video URL"}
                      type={"videos"}
                      name={[field.name, "video"]}
                      update={update}
                    />
                    <BackendUpload
                      label={"Audio URL"}
                      type={"photos"}
                      name={[field.name, "audio"]}
                      update={update}
                    />
                  </Card>
                ))}

                <Button type="dashed" onClick={() => add()} block>
                  + Add Song
                </Button>
              </div>
            )}
          </Form.List>

          <Button
            type="primary"
            htmlType="submit"
            loading={isPending}
            style={{
              marginTop: 20,
            }}
          >
            Submit
          </Button>
        </Form>
      </Modal>
    </div>
  );
};

export default AddAlbum;
